import { useTranslation } from '@dagens/frontend-i18n';
import { useEffect, useState } from 'react';
import { Button, ButtonGroup, Icon, Link } from '@dagens/carrot';
import { DeliveryType } from '../../../types/Logistics';
import CancelOrder from './CancelOrder';
import ConsumerContactInfo from './ConsumerContactInfo';
import OrderDeliveryInformation from './OrderDeliveryInformation';
import Typography from '@carrot-deprecated/typography';
import Callout from '@carrot-deprecated/callout';
import Theme from '@carrot-deprecated/theme';
import Container from '@carrot-deprecated/container';
import { ButtonLink } from '_common/components/button/Button.style';
import ExpandableCallout from '_common/components/callout/ExpandableCallout';
import OrderReference from '_common/components/order-reference/OrderReference';
import OrderLineTable from '_common/components/orderline-table/OrderLineTable';
import { useAppDispatch, useAppSelector } from '_common/hooks/reduxHooks';
import OrderAdjustedFeedback from '_common/pages/order/OrderAdjustedFeedback';
import OrderMessage from '_common/pages/order/OrderMessage';
import OrderPageHeaderSection from '_common/pages/order/OrderPageHeaderSection';
import { getUserFirstName } from '_common/reducers/auth';
import {
  hasOrderLinesBeenUpdated,
  isActive,
  isDeliveredToConsumer,
  isOpened,
  isOrderInvoiceSentToDagens,
  isOrderReadOnly,
  isOrderSmall,
  postAndUpdateOrderStatuses,
  resetToOriginalOrderLines
} from '_common/reducers/orders';
import { Page } from '@components/page';
import {
  patchOrderDeliveryFee,
  postOrderLine,
  UpdateOrderDeliveryFeePayload,
  UpdateOrderLinePayload
} from 'api';
import { Order, OrderKind } from 'types/Order';
import { PRODUCER_ORDER_OVERVIEW_PAGE_VIEW, track } from 'utils/mixpanel';

type IProducerOrderPage = {
  order: Order;
  orderAndAdjustments: OrderKind[];
  onUpdateOrder: () => Promise<void>;
};

const SmallOrderCallout = ({ order }: { order: Order }) => {
  const { t } = useTranslation();

  if (
    !isOrderReadOnly(order) &&
    !isDeliveredToConsumer(order) &&
    isOrderSmall(order)
  ) {
    return (
      <ExpandableCallout
        expandedContent={
          <Typography variant="paragraph">
            {t('producer:SmallOrderCalloutText', {
              firstName: getUserFirstName(order.consumer)
            })}
          </Typography>
        }
      >
        <Typography variant="paragraphBold">
          {t('producer:SmallOrderCalloutHeader')}
        </Typography>
      </ExpandableCallout>
    );
  }
  return null;
};

const ProducerOrderPage = ({
  order,
  orderAndAdjustments,
  onUpdateOrder
}: IProducerOrderPage) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [addingDeliveryFee, setAddingDeliveryFee] = useState(false);
  const [loadingDeliveryFee, setLoadingDeliveryFee] = useState(false);
  const { defaultProducerDeliveryFee } = useAppSelector(
    ({ auth: { selfDeliveryFees } }) => {
      return {
        defaultProducerDeliveryFee: selfDeliveryFees?.at(0)
      };
    }
  );

  const onUpdateOrderLine = async (payload: UpdateOrderLinePayload) => {
    await postOrderLine(payload);
    await onUpdateOrder();
  };

  const onUpdateDeliveryFee = async (
    orderId: string,
    payload: UpdateOrderDeliveryFeePayload
  ) => {
    await patchOrderDeliveryFee(orderId, payload);
    await onUpdateOrder();
  };

  const onAddDeliveryFee = async () => {
    if (!defaultProducerDeliveryFee) {
      setAddingDeliveryFee(v => !v);
      return;
    }

    setLoadingDeliveryFee(true);
    await onUpdateDeliveryFee(order._id, {
      price: defaultProducerDeliveryFee.price
    });
    setAddingDeliveryFee(false);
  };

  const onCancel = () => {
    dispatch(postAndUpdateOrderStatuses([order._id], 'cancelled'));
    onUpdateOrder();
  };

  useEffect(() => {
    if (order) {
      track(PRODUCER_ORDER_OVERVIEW_PAGE_VIEW);
      if (!isOpened(order)) {
        dispatch(postAndUpdateOrderStatuses([order._id], 'openedByProducer'));
      }
    }
  }, [order]);

  const { _id: orderId, consumer, orderLines } = order;
  const isOrderDeliveredToConsumer = isDeliveredToConsumer(order);
  const isReadOnly = isOrderReadOnly(order);
  const isOrderActive = isActive(order);
  const showAddOrderLineButton = !isReadOnly && !isOrderDeliveredToConsumer;
  const showAddDeliveryFeeButton =
    !isReadOnly &&
    !isOrderDeliveredToConsumer &&
    order.deliveryType === DeliveryType.SELF_DELIVERY &&
    !order.producerDeliveryFee?.valueAtTimeOfOrder &&
    !order.producerDeliveryFee?.finalValue;
  const showMadeSignificantChangesCallout =
    hasOrderLinesBeenUpdated(orderLines) && !isReadOnly;
  const orderIsAdjusted = orderAndAdjustments.length > 1;
  const showOrderAdjustmentLink =
    !orderIsAdjusted && isOrderInvoiceSentToDagens(order);

  return (
    <Page>
      <OrderPageHeaderSection order={order} />
      <ConsumerContactInfo consumer={consumer} />
      <Container mb="s">
        <OrderMessage order={order} pointOfView="producer" />
        <SmallOrderCallout order={order} />
      </Container>
      <OrderLineTable
        orderId={orderId}
        orderLines={orderLines}
        originalOrderLines={resetToOriginalOrderLines(orderLines)}
        orderDeliveryFee={order.producerDeliveryFee}
        orderDeliveryType={order.deliveryType}
        isReadOnly={isReadOnly}
        onUpdateOrder={onUpdateOrder}
        onSaveOrderLine={onUpdateOrderLine}
        onSaveDeliveryFee={onUpdateDeliveryFee}
        addingDeliveryFee={addingDeliveryFee}
        onFinishAddingDeliveryFee={() => setAddingDeliveryFee(false)}
      />
      <Container mt="s">
        <ButtonGroup.NoWrap>
          {showAddOrderLineButton && (
            <Link to={`/orders/${order.secret}/add-orderline`} variant="ghost">
              {t('producer:AddOrderLine')}
              <Icon icon="add" size="small" />
            </Link>
          )}
          {showAddDeliveryFeeButton &&
            !addingDeliveryFee &&
            (loadingDeliveryFee ? (
              <Container ml="s">
                {t('producer:AddingDefaultDeliveryFee')}
              </Container>
            ) : (
              <Button variant="ghost" onClick={onAddDeliveryFee}>
                {t('producer:AddDeliveryFee')}
                <Icon icon="add" size="small" />
              </Button>
            ))}
        </ButtonGroup.NoWrap>
      </Container>
      <OrderReference
        orderId={orderId}
        orderReference={order.orderReference}
        readOnly={isReadOnly}
        my="xl"
        onReferenceChange={onUpdateOrder}
      />
      {showMadeSignificantChangesCallout && (
        <Callout mt="s">
          <Typography variant="paragraph" className="no-print">
            {t(
              'producer:YouHaveMadeSignificantChangesToThisOrderRememberToInformCustomer',
              { firstName: getUserFirstName(consumer) }
            )}
          </Typography>
        </Callout>
      )}
      <OrderAdjustedFeedback
        order={order}
        orderAndAdjustments={orderAndAdjustments}
      />
      {isOrderActive && (
        <Container mt="xl">
          <OrderDeliveryInformation order={order} />
        </Container>
      )}

      {!isReadOnly && <CancelOrder order={order} onCancel={onCancel} />}
      {showOrderAdjustmentLink && (
        <ButtonLink
          variant="secondary"
          my={Theme.Spacings.xl}
          to={{
            pathname: `/orders/${order._id}/adjust`
          }}
          state={{ returnPath: true }}
        >
          {t('producer:OrderAdjustments.createOrderAdjustment')}
        </ButtonLink>
      )}
    </Page>
  );
};

export default ProducerOrderPage;
